import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, pipe } from 'rxjs';
import { IUser, IUserMessengerList, IUserNotification, IEmail, IPostForgot } from '../models';
import { UserEndpoint } from '../endpoints/user';
import { map, tap } from 'rxjs/operators';
import { Authentication } from './authentication';
import { StringUtil } from '@shared/utils';

/**
 * ### **UserService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes, en este caso se encarga de de compartir las funciones
 * hacen diferentes peticiones o llamados a distintos API's que genera el backend.Algunos componentes que hacen uso de este servicio son:
 *  * {@link ChangePasswordComponent}
 */
@Injectable({
  providedIn: 'root',
})
export class User {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(Authentication);

  /**
   * Función que incia junto con el servicio para provver de las funcionalidades requeridas para ejecutar el resto de acciones.
   * @param http Parametro que hace uso de las funciones de HttpClient
   * @param authService Parametro que es proporcionada con las funciones de AuthenticationService.
   */
  constructor() {}

  /**
   * Función que hace una petición de tipo get para obtener la informacion de un usuario en específico.
   * @param id Parametro que recibira el id del usuario.
   */
  get(id: number): Observable<IUser> {
    return this.http.get<IUser>(`${UserEndpoint.userRest}${id}/`);
  }

  /**
   *  Función que hace un apeticion d etipo post al servidor, esto para agregar un usuario a la base de datos.
   *  @param user Objeto de tipo IUser que contine la información del usuario.
   */
  add(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(UserEndpoint.userRest, user);
  }

  /**
   * Función encargada de actualizar la informacion del usuario, para esto hace una peticion de tipo patch que es conocida como una
   * actualización parcial ya que no necesita de todos los datos para ejecutarse.
   *
   * @param id Parametro que recibe el id del usuario a editar.
   * @param user Objeto con la información del usuario a actualizar.
   */
  edit(id: number, user: Partial<IUser>): Observable<IUser> {
    return this.http.patch<IUser>(`${UserEndpoint.userRest}${id}/`, user).pipe(
      tap((response) => {
        this.auth.onUpdateUser.next(response);
      })
    );
  }

  /**
   * Función encargada de obtener las categorias de mensaje a las cuales el usuario se puede suscribir.
   */
  categories_messenger(): Observable<Array<IUserMessengerList>> {
    return this.http.get<Array<IUserMessengerList>>(UserEndpoint.categoriesmessenger);
  }

  /**
   * Función encargada de recuperar la cuenta del usuario en caso de haber olvidado la contraseña.
   *
   * @param email Objeto que contiene el email del usuario.
   */
  forgot(email: IEmail): Observable<any> {
    return this.http.post<any>(UserEndpoint.forgot, email);
  }

  /**
   * Función encargada de validar si el nombre de usuario ya existe.
   * @param username Nombre del usuario al cual se le va  ahacer la validación.
   */
  validUniqueUSer(username: string): Observable<any> {
    return this.http.get(StringUtil.format(UserEndpoint.validUniqueUser, username));
  }

  /**
   * Función encargada de validar si el correo electronico ya tiene asociado otro usuario.
   * @param email Parametro que recibe el email a validar.
   */
  validUniqueEmail(email: string): Observable<any> {
    return this.http.get(StringUtil.format(UserEndpoint.validUniqueEmail, email));
  }

  /**
   * Función encargada de confirmar al usuario que ha olvidado la contraseña, además, ejecutar el cambio de la misma.
   * @param body Objet que recibe la nueva contraseña y el token de verificación.
   */
  forgotConfirm(body: IPostForgot): Observable<any> {
    return this.http.post<any>(UserEndpoint.forgotConfirm, body);
  }

  /**
   *  Función encargada de obtener las notificaciones del usuario, para esto hace una petición get al servidor.
   * @param user Parametro que contiene el id del usuario.
   */
  notifications(user: number): Observable<Array<IUserNotification>> {
    return this.http.get<Array<IUserNotification>>(
      StringUtil.format(UserEndpoint.notifications, user)
    );
  }

  /**
   * Función encargada de obtener los tipos de documentos que se pueden registrar
   */
  getDocumentTypes(): Observable<any> {
    return this.http
      .get<any>(UserEndpoint.documentTypes)
      .pipe(map((response: any) => response.results));
  }

  /**
   * Function responsible for updating the user's profile image.
   * This makes a PATCH request using FormData, allowing the image file to be sent as multipart/form-data.
   * Only the fields present in the FormData will be updated on the server.
   *
   * @param id The ID of the user whose profile image is being updated.
   * @param formData A FormData object containing the profile image file under the key 'image_profile'.
   */
  updateProfileImage(id: number, formData: FormData): Observable<IUser> {
    return this.http.patch<IUser>(`${UserEndpoint.userRest}${id}/`, formData).pipe(
      tap((response) => {
        this.auth.onUpdateUser.next(response);
      })
    );
  }
}
